/*
  TIG 9 Oct 2026. PDP guide links.

  - "Size guide" opens the theme's existing overlay (#size-guide-overlay).
  - "Fit guide" opens /pages/denim-fit-guide in a modal, in an iframe loaded on first
    open. Same origin, so the header, announcement bar, footer and cart inside the frame
    are hidden to leave just the guide.
  - When the size guide opens, any leg length columns are lifted out of the main table
    into their own table underneath, because inside-leg values sitting in the same grid
    as waist and hip rows do not read as a separate choice.
*/
class HeraPdpGuides extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.bindButtons(this);
    const fitOverlayEl = document.getElementById('hera-fit-guide-overlay');
    if (fitOverlayEl) this.bindButtons(fitOverlayEl);
    this.bindThemeLinks();
    this.init();
  }

  bindButtons(root) {
    root.querySelectorAll('[data-guide]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'yes';
      btn.addEventListener('click', () => {
        if (btn.dataset.guide === 'fit') { this.closeSize(); this.openFit(); }
        else { this.closeFit(); this.openSize(); }
      });
    });
  }

  // The sizing details block has its own row ("Unsure about fit? Explore our denim fit
  // guide") linking to the page. Open it in the same panel instead of leaving the PDP.
  // Delegated from the document because that block renders after this element, so there
  // is nothing to bind to at upgrade time. The href carries a fit anchor, e.g. #wide-leg.
  bindThemeLinks() {
    if (document.documentElement.dataset.heraFitLinks === 'on') return;
    document.documentElement.dataset.heraFitLinks = 'on';
    document.addEventListener('click', (e) => {
      const link = e.target.closest && e.target.closest('a[href*="denim-fit-guide"]');
      if (!link || link.closest('#hera-fit-guide-overlay')) return;
      e.preventDefault();
      const hash = (link.getAttribute('href') || '').split('#')[1] || '';
      this.closeSize();
      this.openFit(hash);
    });
  }

  init() {
    this.fitOverlay = document.getElementById('hera-fit-guide-overlay');
    if (this.fitOverlay) {
      // the PDP info column is sticky, which traps position:fixed children inside it and
      // leaves the gallery underneath still catching hover. Reparent to the body.
      if (this.fitOverlay.parentElement !== document.body) document.body.appendChild(this.fitOverlay);
      this.fitOverlay.addEventListener('click', (e) => {
        if (e.target === this.fitOverlay || e.target.closest('.hera-fit-overlay__close')) this.closeFit();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.fitOverlay.classList.contains('is-open')) this.closeFit();
      });
    }
  }

  // --- size guide -----------------------------------------------------------
  openSize() {
    const embedded = document.querySelector('size-guide .size-guide-button');
    if (embedded) {
      embedded.click();
    } else {
      const overlay = document.getElementById('size-guide-overlay');
      if (overlay) overlay.classList.add('overlay-open');
    }
    // the theme fills the overlay on click, so wait a tick before rearranging it
    setTimeout(() => { this.splitLegLengths(); this.addCrossLink(); }, 60);
    // and again after a unit switch (cm / inch), which swaps the visible table
    const overlay = document.getElementById('size-guide-overlay');
    if (overlay && !overlay.dataset.legWatch) {
      overlay.dataset.legWatch = 'on';
      overlay.addEventListener('click', () => setTimeout(() => { this.splitLegLengths(); this.addCrossLink(); }, 60));
    }
  }

  closeSize() {
    const overlay = document.getElementById('size-guide-overlay');
    if (overlay) overlay.classList.remove('overlay-open');
  }

  // Georgia asked for the two guides to link to each other, so the size guide carries a
  // link to the fit guide and the fit modal carries one back.
  addCrossLink() {
    if (!this.dataset.fitUrl) return;
    const overlay = document.getElementById('size-guide-overlay');
    const content = overlay && overlay.querySelector('.size-guide-content');
    if (!content || content.querySelector('.hera-guides__cross')) return;
    const wrap = document.createElement('div');
    wrap.className = 'hera-guides__cross';
    wrap.innerHTML = 'Not sure which fit suits you? <button type="button" data-guide="fit">See the denim fit guide</button>';
    content.appendChild(wrap);
    this.bindButtons(wrap);
  }

  splitLegLengths() {
    const overlay = document.getElementById('size-guide-overlay');
    if (!overlay) return;

    // the theme re-copies the guide markup on open and on a unit switch, which can leave
    // leftover copies behind. Keep only the one sitting directly after a table.
    overlay.querySelectorAll('.hera-leg-table').forEach((wrap) => {
      // a wrap inside another wrap is a copy of our own output, not real content
      if (wrap.parentElement && wrap.parentElement.closest('.hera-leg-table')) { wrap.remove(); return; }
      const prev = wrap.previousElementSibling;
      if (!prev || prev.tagName !== 'TABLE') wrap.remove();
    });

    // cm and inch live as separate tables in the same overlay, so do every one of them
    [...overlay.querySelectorAll('table')].forEach((table) => {
      if (table.dataset.legSplit === 'done') return;
      // never re-split the leg table we generated: its own headings match the test
      if (table.closest('.hera-leg-table')) return;
      const firstRow = table.querySelector('tr');
      if (!firstRow) return;

      const headCells = [...firstRow.children];
      const indexes = headCells
        .map((cell, i) => ({ i, text: cell.textContent.trim().toLowerCase() }))
        .filter((c) => c.text.includes('leg length') || c.text.includes('inside leg'))
        .map((c) => c.i);
      if (indexes.length < 2) return;

      const legRows = [];
      [...table.querySelectorAll('tr')].forEach((row) => {
        const cells = [...row.children];
        const picked = indexes.map((i) => (cells[i] ? cells[i].textContent.trim() : ''));
        if (picked.some((v) => v !== '')) legRows.push(picked);
        [...indexes].sort((a, b) => b - a).forEach((i) => { if (cells[i]) cells[i].remove(); });
      });
      table.dataset.legSplit = 'done';
      if (legRows.length < 2) return;

      const wrap = document.createElement('div');
      wrap.className = 'hera-leg-table';
      const heading = document.createElement('h3');
      heading.textContent = 'Leg length';
      const newTable = document.createElement('table');
      const body = document.createElement('tbody');
      legRows.forEach((cells, rowIndex) => {
        const tr = document.createElement('tr');
        cells.forEach((value) => {
          const cell = document.createElement(rowIndex === 0 ? 'th' : 'td');
          cell.textContent = value;
          tr.appendChild(cell);
        });
        body.appendChild(tr);
      });
      newTable.appendChild(body);
      wrap.appendChild(heading);
      wrap.appendChild(newTable);
      // insert inside the same unit panel so it shows and hides with its tab
      table.parentNode.insertBefore(wrap, table.nextSibling);
    });
  }

  // --- fit guide ------------------------------------------------------------
  // Shopify sends frame-ancestors 'none', so the page cannot be iframed even on the
  // same origin. Instead the page is fetched and its content injected, carrying over
  // the stylesheets and re-running its scripts.
  async openFit(wantedFit) {
    if (!this.fitOverlay) return;
    this.fitOverlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    if (this._loaded) { this.selectFit(wantedFit); return; }
    this._loaded = true;

    const body = this.fitOverlay.querySelector('.hera-fit-overlay__body');
    const url = this.dataset.fitUrl || '/pages/denim-fit-guide';
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('status ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        const href = link.getAttribute('href');
        if (href && !document.querySelector('link[href="' + href + '"]')) {
          const copy = document.createElement('link');
          copy.rel = 'stylesheet';
          copy.href = href;
          document.head.appendChild(copy);
        }
      });

      const main = doc.querySelector('#MainContent') || doc.querySelector('main');
      if (!main) throw new Error('no main content');
      main.querySelectorAll('#shopify-section-announcement-bar, #shopify-section-hera-header, #shopify-section-hera-footer, [id*="mini-cart"]').forEach((el) => el.remove());

      body.innerHTML = '';
      const holder = document.createElement('div');
      holder.className = 'hera-fit-overlay__page';
      holder.innerHTML = main.innerHTML;
      body.appendChild(holder);
      this.runScripts(main, holder);
      this.selectFit(wantedFit);
    } catch (e) {
      body.innerHTML = '<p class="hera-fit-overlay__loading">The fit guide could not load here. <a href="' + url + '">Open it in a new page</a>.</p>';
    }
  }

  // Open on the fit the shopper asked for, matching "wide-leg" to the WIDE LEG tab.
  selectFit(wantedFit) {
    if (!wantedFit || !this.fitOverlay) return;
    const want = wantedFit.replace(/[-_]/g, ' ').trim().toLowerCase();
    setTimeout(() => {
      const target = document.getElementById(wantedFit);
      const tabs = [...this.fitOverlay.querySelectorAll('button, [role="tab"], a')];
      const tab = tabs.find((t) => t.textContent.trim().toLowerCase() === want);
      if (tab) tab.click();
      else if (target) target.scrollIntoView({ block: 'start' });
    }, 400);
  }

  // Section scripts bind on DOMContentLoaded, which has long since fired, so run those
  // handlers immediately while the injected scripts execute.
  runScripts(source, holder) {
    const originalAdd = document.addEventListener.bind(document);
    document.addEventListener = (type, handler, options) => {
      if (type === 'DOMContentLoaded' && typeof handler === 'function') {
        try { handler(); } catch (err) { /* a section script failing must not stop the rest */ }
        return;
      }
      return originalAdd(type, handler, options);
    };
    try {
      const loaded = new Set([...document.querySelectorAll('script[src]')].map((s) => (s.getAttribute('src') || '').split('?')[0]));
      source.querySelectorAll('script').forEach((old) => {
        // skip theme scripts the product page already loaded: re-running them throws
        // (fostr-card-carousel.js declares a top-level cardSwiper)
        const src = (old.getAttribute('src') || '').split('?')[0];
        if (src && loaded.has(src)) return;
        const script = document.createElement('script');
        [...old.attributes].forEach((a) => script.setAttribute(a.name, a.value));
        // wrap inline code so page-level consts do not clash with the PDP's own
        // (the carousel script declares cardSwiper, which already exists here)
        if (!old.src && old.textContent.trim()) {
          script.textContent = '(function(){' + old.textContent + '})();';
        }
        holder.appendChild(script);
      });
    } finally {
      setTimeout(() => { document.addEventListener = originalAdd; }, 0);
    }
  }

  closeFit() {
    if (!this.fitOverlay) return;
    this.fitOverlay.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('hera-pdp-guides')) {
  customElements.define('hera-pdp-guides', HeraPdpGuides);
}
